import React from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useContent } from "../lib/content";
import { Car, MapPin, Clock, Mail, Phone } from "lucide-react";

// Eurobetom-style opening mask — polished. Anonymous visitors land here at /;
// logged-in users go straight to /catalog or /admin. The tiny "©" in the
// footer is the discreet partner-webshop entry (→ /login).
//
// Editable strings come from Tartalomkezelő (Admin → Tartalom) under the
// landing_* keys.

// Curated workshop photos (verified reachable on Unsplash CDN).
const STRIP_PHOTOS = [
  "https://images.unsplash.com/photo-1487754180451-c456f719a1fc?auto=format&fit=crop&w=900&q=70",
  "https://images.unsplash.com/photo-1486006920555-c77dcf18193c?auto=format&fit=crop&w=900&q=70",
  "https://images.unsplash.com/photo-1632823469850-2f77dd9c7f93?auto=format&fit=crop&w=900&q=70",
  "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=900&q=70",
  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=900&q=70",
];

const HERO_BG =
  "https://images.unsplash.com/photo-1606577924006-27d39b132ae2?auto=format&fit=crop&w=1920&q=80";

export default function Landing() {
  const { user } = useAuth();
  const { t } = useContent();

  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/catalog"} replace />;
  }

  const services = [
    t("landing_service_1", "Non-stop javítási szolgáltatás"),
    t("landing_service_2", "Vontatási szolgáltatás 24/7"),
    t("landing_service_3", "Specializált cég német járműgyártók számára"),
    t("landing_service_4", "Autóvillamosság"),
    t("landing_service_5", "Gyorsszervíz"),
    t("landing_service_6", "Klíma töltés és javítás"),
    t("landing_service_7", "Gumiszerelés"),
  ].filter(Boolean);

  return (
    <div className="landing-root" data-testid="landing-page">
      {/* ============ TOP BAND ============ */}
      <header className="landing-topband" data-testid="landing-topband">
        <div className="landing-logo">
          <div className="landing-logo-icon" aria-hidden>
            <Car size={28} strokeWidth={2.4} />
          </div>
          <div className="landing-logo-text">
            <span className="landing-logo-line1">{t("landing_company_name", "VAG SERVICE")}</span>
            <span className="landing-logo-line2">{t("landing_tagline", "NON-STOP SERVICES")}</span>
          </div>
        </div>
        <div className="landing-strip" aria-hidden>
          {STRIP_PHOTOS.map((src, i) => (
            <div
              key={i}
              className="landing-strip-cell"
              style={{ backgroundImage: `url("${src}")` }}
              data-testid={`landing-strip-${i}`}
            />
          ))}
        </div>
      </header>

      {/* ============ HERO ============ */}
      <main
        className="landing-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(247,249,252,0.88), rgba(232,238,247,0.95)), url("${HERO_BG}")`,
        }}
        data-testid="landing-hero"
      >
        <div className="landing-hero-inner">
          <div className="landing-services-wrap">
            <span className="landing-services-eyebrow">Szolgáltatások</span>
            <ul className="landing-services" data-testid="landing-services">
              {services.map((s, i) => (
                <li key={i} className="landing-service-line">
                  <span className="landing-dash" aria-hidden>—</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          <aside className="landing-contact" data-testid="landing-contact">
            <div className="landing-contact-eyebrow">Elérhetőség</div>
            <div className="landing-contact-row">
              <span className="landing-contact-ico"><MapPin size={18} strokeWidth={2} /></span>
              <span>{t("landing_address", "Budapest, Asztalos Sándor 5-6.")}</span>
            </div>
            <div className="landing-contact-row">
              <span className="landing-contact-ico"><Clock size={18} strokeWidth={2} /></span>
              <span>{t("landing_hours", "Nyitva H–P: 08:00–16:00 · SZ-V: Zárva")}</span>
            </div>
            <div className="landing-contact-row landing-contact-phone">
              <span className="landing-contact-ico"><Phone size={18} strokeWidth={2} /></span>
              <a href={`tel:${t("landing_phone", "+36 20 9 40 40 04")}`} data-testid="landing-phone-link">
                {t("landing_phone", "+36 20 9 40 40 04")}
              </a>
            </div>
            <div className="landing-contact-row landing-contact-email">
              <span className="landing-contact-ico"><Mail size={18} strokeWidth={2} /></span>
              <a href={`mailto:${t("landing_email", "info@vagparts.hu")}`} data-testid="landing-email-link">
                {t("landing_email", "info@vagparts.hu")}
              </a>
            </div>
          </aside>
        </div>
      </main>

      {/* ============ FOOTER (the © is the webshop entry) ============ */}
      <footer className="landing-footer" data-testid="landing-footer">
        <ul className="landing-copyright">
          <li>
            <Link
              to="/login"
              className="landing-shop-entry"
              title="Belépés a partner webshopba"
              data-testid="landing-shop-entry"
            >
              ©
            </Link>{" "}
            {t("landing_company_name", "VAG SERVICE")}
          </li>
          <li>Design</li>
        </ul>
      </footer>
    </div>
  );
}
